/**
 * Plexus Design System Color Tokens
 * Centralized color system combining official Plexus tokens with theme-specific colors
 */

// Brand color family - Primary brand colors for logos, headers, and key UI elements
// Official brand values: 400 (#0066FF) is the main accent for kickers, links and
// buttons; 500 (#2F88FF) is its dark-background counterpart, which is more legible
// there. Prefer 500 over 400 for brand-coloured elements on dark surfaces.
// The remaining steps are shades/tints of #0066FF, preserving the ramp's original
// dark-to-light progression so hover and surface-tint states stay differentiated.
export const brand = {
  100: '#002966', // Darkest brand - for text on light backgrounds
  200: '#003D99', // Dark brand - for active states
  300: '#0052CC', // Medium-dark brand - for hover states
  400: '#0066FF', // Primary brand - main accent (Plexus Blue)
  500: '#2F88FF', // Brand accent for dark backgrounds - more legible than 400
  600: '#66A3FF', // Light brand - for subtle backgrounds
  700: '#99C2FF', // Lighter brand - for very subtle backgrounds
  800: '#CCE0FF', // Very light brand - for surface tints
  900: '#E5F0FF', // Lightest brand - for minimal tints
};

// Caution color family - For warnings, alerts, and attention-grabbing elements
export const caution = {
  100: '#614D00', // Darkest caution - for text
  200: '#947500', // Dark caution - for active states  
  300: '#C79E00', // Medium-dark caution - for hover states
  400: '#FCC800', // Primary caution - main warning color
  500: '#FFD42E', // Medium-light caution - for accents
  600: '#FFDE61', // Light caution - for subtle backgrounds
  700: '#FFE994', // Lighter caution - for very subtle backgrounds
  800: '#FFEDA8', // Very light caution - for surface tints
  900: '#FFF8DB', // Lightest caution - for minimal tints
};

// Neutral color family - For text, borders, and general UI elements
export const neutral = {
  100: '#26262C', // Darkest neutral - for primary text on light backgrounds
  200: '#555562', // Dark neutral - for secondary text and icons
  300: '#666675', // Medium-dark neutral - for muted text and borders
  400: '#808090', // Medium neutral - for placeholder text and disabled states
  500: '#B8B8C4', // Medium-light neutral - for subtle borders and dividers
  600: '#E0E0E3', // Light neutral - for borders and inactive states
  700: '#E6E6E6', // Lighter neutral - for subtle backgrounds and dividers
  800: '#ECECEE', // Very light neutral - for surface backgrounds
  900: '#F3F3F4', // Lightest neutral - for page and container backgrounds
};

// Positive color family - For success states, confirmations, and primary actions
export const positive = {
  100: '#000F07', // Darkest positive - for text on light backgrounds
  200: '#00421E', // Dark positive - for active states
  300: '#007536', // Medium-dark positive - for hover states
  400: '#00AA4E', // Primary positive - main success/primary color (active steps)
  500: '#00DB65', // Medium-light positive - for accents
  600: '#0FFF7D', // Light positive - for subtle backgrounds
  700: '#42FF99', // Lighter positive - for very subtle backgrounds
  800: '#75FFB4', // Very light positive - for surface tints
  900: '#D1FFE6', // Lightest positive - for minimal tints
};

// Urgent color family - For errors, destructive actions, and critical alerts
export const urgent = {
  100: '#470600', // Darkest urgent - for text
  200: '#7A0B00', // Dark urgent - for active states
  300: '#AB1000', // Medium-dark urgent - for hover states
  400: '#E01500', // Primary urgent - main error color
  500: '#FF2A14', // Medium-light urgent - for accents
  600: '#FF5947', // Light urgent - for subtle backgrounds
  700: '#FF877A', // Lighter urgent - for very subtle backgrounds
  800: '#FFB5AD', // Very light urgent - for surface tints
  900: '#FFE3E0', // Lightest urgent - for minimal tints
};

// Surface color family - For backgrounds and containers
export const surface = {
  50: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F3F4F6',
  300: '#E5E7EB',
  400: '#D1D5DB',
  500: '#9CA3AF',
  600: '#6B7280',
  700: '#374151',
  800: '#1F2937',
  900: '#111827'
};

// Base colors
export const white = '#ffffff';
export const black = '#000000';

// Consolidated Plexus Colors object
export const plexusColors = {
  brand,
  caution,
  neutral,
  positive,
  urgent,
  surface,
  white,
  black,
};

// Custom tokens for enhanced UI components (non-Plexus extensions)
export const customTokens = {
  // Enhanced neutral backgrounds for minimal UI
  neutralBg: {
    subtle: '#FAFAFA', // Very light background for default option states
    hover: '#F5F5F5',  // Slightly darker for hover states
  },
  // Enhanced text colors for better readability
  text: {
    description: '#4A5568', // Darker gray for better description text readability
    muted: '#718096',       // Medium gray for less important text
  }
};

// Theme-specific colors for theme switching functionality
export const themeColors = {
  green: {
    headerBg: positive[400], // #00AA4E
    headerText: white,
    bodyBg: white,
    bodyText: neutral[100] // Use darkest neutral for text readability
  },
  neutral: {
    headerBg: '#F6F6F6', 
    headerText: neutral[100], // Use darkest neutral for text readability
    bodyBg: '#f3f3f4',
    bodyText: neutral[100] // Use darkest neutral for text readability
  }
};
